/**
 * Parses a permissive time string into "HH:MM" 24-hour format.
 *
 * Accepts:
 *   "0800", "800", "8", "08:00", "8:00", "8a", "8am", "8 AM"
 *   "1430", "14:30", "2:30p", "2:30pm", "230p", "2p", "2 PM"
 *
 * Returns null when the input is empty or unparseable so callers can fall
 * back to the previous value.
 */
export function parseTime(raw) {
  if (raw == null) return null;
  let s = String(raw).trim().toLowerCase();
  if (!s) return null;

  // AM/PM detection. Strip the marker before parsing digits.
  let suffix = null;
  if (s.endsWith("am") || s.endsWith("a")) {
    suffix = "am";
    s = s.replace(/\s*(am|a)$/, "").trim();
  } else if (s.endsWith("pm") || s.endsWith("p")) {
    suffix = "pm";
    s = s.replace(/\s*(pm|p)$/, "").trim();
  }

  let hh, mm;
  if (s.includes(":")) {
    const [h, m] = s.split(":");
    hh = parseInt(h, 10);
    mm = parseInt(m, 10);
  } else {
    // No colon — interpret as packed digits.
    // 1-2 digits = hour only; 3-4 digits = HMM or HHMM.
    const digits = s.replace(/\D/g, "");
    if (!digits) return null;
    if (digits.length <= 2) {
      hh = parseInt(digits, 10);
      mm = 0;
    } else if (digits.length === 3) {
      hh = parseInt(digits.slice(0, 1), 10);
      mm = parseInt(digits.slice(1), 10);
    } else if (digits.length === 4) {
      hh = parseInt(digits.slice(0, 2), 10);
      mm = parseInt(digits.slice(2), 10);
    } else {
      return null;
    }
  }
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (mm < 0 || mm > 59) return null;
  if (suffix === "am") {
    if (hh === 12) hh = 0;
    else if (hh < 1 || hh > 12) return null;
  } else if (suffix === "pm") {
    if (hh === 12) hh = 12;
    else if (hh >= 1 && hh <= 11) hh += 12;
    else return null;
  } else {
    if (hh < 0 || hh > 23) return null;
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(hh)}:${pad(mm)}`;
}

/** Replaces the HH:MM portion of an ISO timestamp with `hhmm` (string "HH:MM").
 *
 * Wall-clock convention: positions 11-15 of the ISO string hold "HH:MM".
 * We do PURE string surgery — never go through `new Date()` here, because
 * `Date.setHours()` interprets the input as the browser's LOCAL timezone
 * while `Date.toISOString()` writes back in real UTC. Mixing those is how
 * a user typing "14:00" in Adelaide ends up with "04:30Z" in the database.
 *
 * See /lib/format.js for the full convention rationale.
 */
export function setIsoTime(iso, hhmm) {
  if (!iso || !hhmm) return iso;
  const s = String(iso);
  // Sanity: ISO must be at least "YYYY-MM-DDTHH:MM" and have the T separator.
  if (s.length < 16 || s[10] !== "T") return iso;
  // Replace positions 11-15 (HH:MM); leave seconds + TZ suffix untouched.
  return `${s.slice(0, 11)}${hhmm}${s.slice(16)}`;
}
